import { useEffect, useMemo, useState } from 'react';

import { isExperimentEnabled, expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

export enum BriefingNudgeExperimentState {
	CONTROL = 'control',
	NUDGE = 'nudge',
	NUDGE_AUTOPLAY = 'nudge_autoplay',
}

type AudioFeatureFlags = {
	isAIPolicyAndBrowserSupportEnabled: boolean;
	isAudioV3Enabled: boolean;
	isAudioBylineEntrypointEnabled: boolean;
	audioBriefingNudgeExperimentState: BriefingNudgeExperimentState;
};

export const useAudioFeatureFlags = (): AudioFeatureFlags => {
	const { isAdminHubAIEnabled, isAIEarlyAccessEnabled, isAtlassianHostedLLMOnlyEnabled } =
		useSessionData();

	// Deferred: MediaSource is unavailable during SSR
	const [audioSupportedByBrowser, setAudioSupportedByBrowser] = useState(false);
	useEffect(() => {
		setAudioSupportedByBrowser(
			typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg'),
		);
	}, []);

	const isAHMOnlyEnabled = useMemo(
		() =>
			isAtlassianHostedLLMOnlyEnabled &&
			isExperimentEnabled('confluence_audio_disable_if_ahm_enabled'),
		[isAtlassianHostedLLMOnlyEnabled],
	);

	const isAIPolicyAndBrowserSupportEnabled = useMemo(
		() =>
			isAdminHubAIEnabled && // AdminHub AI is enabled
			isAIEarlyAccessEnabled && // AI Early Access is enabled
			audioSupportedByBrowser && // Audio is supported by browser
			!isAHMOnlyEnabled, // Atlassian Hosted LLM Only is not enabled
		[isAdminHubAIEnabled, isAIEarlyAccessEnabled, audioSupportedByBrowser, isAHMOnlyEnabled],
	);

	const isAudioV3Enabled = useMemo(
		() => isAIPolicyAndBrowserSupportEnabled && isExperimentEnabled('confluence_audio_v3'),
		[isAIPolicyAndBrowserSupportEnabled],
	);

	const isAudioBylineEntrypointEnabled = useMemo(
		() =>
			isAIPolicyAndBrowserSupportEnabled && isExperimentEnabled('confluence_ai_consumption_byline'),
		[isAIPolicyAndBrowserSupportEnabled],
	);

	const audioBriefingNudgeExperimentState = useMemo(() => {
		if (!isAudioV3Enabled) {
			return BriefingNudgeExperimentState.CONTROL;
		}

		if (expValEquals('confluence_audio_v3_briefing_nudge', 'variant', 'nudge')) {
			return BriefingNudgeExperimentState.NUDGE;
		} else if (expValEquals('confluence_audio_v3_briefing_nudge', 'variant', 'nudge_autoplay')) {
			return BriefingNudgeExperimentState.NUDGE_AUTOPLAY;
		} else {
			return BriefingNudgeExperimentState.CONTROL;
		}
	}, [isAudioV3Enabled]);

	return {
		isAIPolicyAndBrowserSupportEnabled,
		isAudioV3Enabled,
		isAudioBylineEntrypointEnabled,
		audioBriefingNudgeExperimentState,
	};
};
